<template>
  <FormLayout
    :has-changes="hasChanges"
    :is-loading="isCreatingNewLocation ? false : isLoading"
    :is-saving="isSaving"
    :title="title"
    :label="archivedChip"
    @on-save="submit"
    @on-dismiss="dismiss"
  >
    <vs-form :key="$route.params.id" ref="form">
      <vs-alert v-if="isErrorSaving" type="error">{{ errorMessage }}</vs-alert>

      <vs-text-input
        :value="formData.location"
        :disabled="item ? !item.canEdit : false"
        label="Name"
        required
        @input="handleUpdateLocationName"
      />

      <LocationAdditionalInfo
        :location="{
          ...formData,
          canEdit: item ? item.canEdit : true,
        }"
        @updated="handleLocationAdditionalInfoUpdate"
      />

      <vs-text-input
        :value="formData.description"
        :disabled="item ? !item.canEdit : false"
        label="Description"
        multiline
        @input="handleUpdateLocationDescription"
      />

      <new-location-picker
        v-if="showParentLocationField"
        :disabled="item ? !item.canEdit : false"
        :autofocus="formData.parentId === null"
        :value="formData.parentId ?? undefined"
        :exclude-location-ids="isCreatingNewLocation ? [] : [parseInt(props.id.toString(), 10)]"
        label="Parent location"
        @input="onParentChange"
      />
      <vs-button
        v-else
        :disabled="item ? !item.canEdit : false"
        label="Add parent location"
        small
        type="secondary"
        @click="parentFlag = true"
      />

      <vs-wrapper v-if="schema && schema.properties">
        <vs-heading>Details</vs-heading>
        <schema-field
          :disabled="item ? !item.canEdit : false"
          :error-schema="errorSchema"
          :form-data="formData ? formData.customData : {}"
          :form-resolver="contextualizedFormResolver"
          :params="{ headingType: 'heading' }"
          :root-schema="schema"
          :schema="schema"
          :ui-schema="uiSchema"
          @change="handleLocationSchemaFieldsUpdate"
        />
      </vs-wrapper>

      <LocationCoordinates
        :location="{
          ...formData,
          canEdit: item ? item.canEdit : true,
        }"
        @updated="handleLocationLatLongUpdate"
      />

      <LocationHubAccess
        v-if="isHubUser"
        :access-grant-required="true"
        :location-id="isCreatingNewLocation ? null : parseInt(props.id.toString(), 10)"
        :parent-location-id="formData.parentId"
        :access-business-unit-ids="formData.accessBusinessUnitIds"
        :class="{
          'focus-hubs-selector': focusHubsSelector,
        }"
        @updated="handleLocationHubAccessUpdate"
      />
      <LocationAccessAndOwnership
        :location="{
          ...formData,
          canEdit: item ? item.canEdit : true,
        }"
        @updated="handleLocationAdditionalInfoUpdate"
      />
      <LocationInventoryEdit
        v-if="isCreatingNewLocation"
        :modelValue="formData.inventory"
        @update:modelValue="(value) => (formData.inventory = value)"
      />
      <TwoStageButton
        v-if="!isCreatingNewLocation"
        :alternate="formData.isArchived ?? false"
        :is-loading="isArchiving || isUnarchiving"
        :is-disabled="isParentLocation"
        alternate-button-text="Unarchive"
        main-button-text="Archive"
        disabled-tooltip-text="Parent locations cannot be archived"
        @click="handleArchiveLocation"
      />
    </vs-form>
  </FormLayout>
</template>

<script setup lang="ts">
import { computed, onMounted, onUnmounted, reactive, ref, watch, watchEffect } from 'vue'
import { useStore } from 'vuex-composition-helpers'
import { useFieldView } from './FieldViewState'
import { isEmpty } from 'lodash'
import { FormContext } from '@/utils/forms/form-context'
import { ContextualizedFormResolver } from '@/utils/forms/contextualized-form-resolver'
import { resolveFormDataForValidation } from '@/utils/forms/form-resolver'
import { validateFormData } from '@/utils/forms/form-validate'
import { useRoute, useRouter } from 'vue-router/composables'
import { useMap } from './mapbox/context'
import type { User as UserAuth0 } from '@auth0/auth0-spa-js/dist/typings/global'
import { getInstance } from '@/auth/auth.js'
import LocationHubAccess from '@/components/locations/LocationHubAccess.vue'
import TwoStageButton from '@/components/common/TwoStageButton.vue'
import LocationCoordinates from '@/components/locations/LocationCoordinates.vue'
import FormLayout from '@/components/common/FormLayout.vue'
import LocationAdditionalInfo from '@/components/locations/LocationAdditionalInfo.vue'
import LocationInventoryEdit from '@/components/locations/LocationInventoryEdit.vue'
import VsForm from '@/components/vision/VsForm.vue'
import {
  useArchiveLocationMutation,
  useCreateLocationMutation,
  useUnarchiveLocationMutation,
  useUpdateLocationMutation,
} from '@/api/account-data/manage-locations'
import { getById, useLocationQuery } from '@/api/locations'
import NewLocationPicker from '@/components/locations/NewLocationPicker.vue'
import { useGetLocationTypesQuery } from '@/api/location-types'
import Location from '@/models/location'
import VsAlert from '@/components/vision/VsAlert.vue'
import VsTextInput from '@/components/vision/Inputs/VsTextInput.vue'
import VsWrapper from '@/components/vision/VsWrapper.vue'
import VsHeading from '@/components/vision/VsHeading.vue'
import LocationAccessAndOwnership from '@/components/locations/LocationAccessAndOwnership.vue'
import { InventoryPickerItem } from '../tickets/StopsAndInventories/InventoryPickers'

interface RedirectAfterSaveToType {
  name: string
  params: {
    source: string
  }
}
interface LocationTypeCustomFieldsSchema {
  schema?: {
    properties: object
  }
  uiSchema?: object
}

const ID_NEW = 'new'

const props = withDefaults(
  defineProps<{
    id?: number | string
    redirectAfterSaveTo?: RedirectAfterSaveToType | null
    dismissTo?: { name: string } | null
    focusHubsSelector?: boolean
  }>(),
  {
    id: 'new', // Avoids exposing ID_NEW outside this component
    redirectAfterSaveTo: null,
    dismissTo: null,
    focusHubsSelector: false,
  },
)

const {
  fieldViewState: state,
  setSelectedLocation,
  setEditMode,
  upsertModifiedLocation,
} = useFieldView()
const store = useStore()
const router = useRouter()
const route = useRoute()
const { map } = useMap()
const auth: { user: UserAuth0 } = getInstance()
const form = ref()
const emit = defineEmits(['dismiss', 'save'])

const parentFlag = ref(false)
const errorSchema = ref({})
const redirectAfterArchivalTo = ref({ name: 'field-view-next' })
const isCreatingNewLocation = computed(() => props.id === ID_NEW)
const idRef = computed(() => {
  if (isCreatingNewLocation.value) {
    return undefined
  } else {
    return parseInt(props.id.toString(), 10)
  }
})
const { data: item, isLoading, refetch } = useLocationQuery(idRef)
const { data: allLocationTypes } = useGetLocationTypesQuery()
const locationType = computed(() =>
  allLocationTypes.value?.find((lt) => lt.id === formData.locationTypeId),
)
const {
  error: updateError,
  isError: isUpdateError,
  mutateAsync: updateLocation,
} = useUpdateLocationMutation()
const {
  error: createError,
  isError: isCreateError,
  mutateAsync: createLocation,
  data: createResponse,
} = useCreateLocationMutation()
const { isLoading: isArchiving, mutateAsync: archiveLocation } = useArchiveLocationMutation()
const { isLoading: isUnarchiving, mutateAsync: unarchiveLocation } = useUnarchiveLocationMutation()

interface FormData {
  location?: string | null
  parentId?: number | null
  description?: string | null
  isArchived?: boolean | null
  latitude?: number | null
  longitude?: number | null
  locationTypeId?: number | null
  colorId?: number | null
  ownerId?: string | null
  radius?: number | null
  customData?: object | null
  accessBusinessUnitIds: string[]
  inventory: InventoryPickerItem[]
  private?: boolean | null
}

const formData = reactive<FormData>({
  location: undefined,
  parentId: undefined,
  description: undefined,
  isArchived: false,
  latitude: undefined,
  longitude: undefined,
  locationTypeId: undefined,
  colorId: undefined,
  ownerId: undefined,
  radius: undefined,
  customData: {},
  accessBusinessUnitIds: [],
  inventory: [],
  private: false,
})

onMounted(() => setEditMode(true))
onUnmounted(() => setEditMode(false))

watchEffect(() => {
  if (props.id !== ID_NEW && !isLoading.value && item.value) {
    // Initial loading - Only run once
    // isLoading will only change from true to false once, at the beginning
    formData.location = item.value.location
    formData.parentId = item.value.parentId
    formData.description = item.value.description
    formData.isArchived = item.value.isArchived
    formData.latitude = item.value.latitude
    formData.longitude = item.value.longitude
    formData.locationTypeId = item.value.locationTypeId
    formData.colorId = item.value.colorId
    formData.ownerId = item.value.ownerId
    formData.radius = item.value.radius
    formData.customData = item.value.customData
    formData.accessBusinessUnitIds = item.value.accessBusinessUnitIds
    formData.private = item.value.private
  } else if (props.id === ID_NEW) {
    const defaults =
      route.params.defaultLat && route.params.defaultLng
        ? {
            lat: Number(route.params.defaultLat),
            lng: Number(route.params.defaultLng),
          }
        : null
    let { lat, lng } = defaults ?? map.value!.getCenter()

    lat = parseFloat(lat.toFixed(8))
    lng = parseFloat(lng.toFixed(8))

    formData.latitude = lat
    formData.longitude = lng

    setSelectedLocation(new Location({ latitude: lat, longitude: lng }))
  }
})

watch(
  () => [state.value.selectedLocation?.latitude, state.value.selectedLocation?.longitude],
  ([newLat, newLng]) => {
    if (newLat !== null && newLng !== null) {
      formData.latitude = parseFloat(newLat!.toFixed(8))
      formData.longitude = parseFloat(newLng!.toFixed(8))
    }
  },
)

const hasChanges = computed(() => {
  if (isCreatingNewLocation.value) {
    return true
  } else if (isLoading.value || !item.value) {
    return false
  } else {
    return (
      formData.location !== item.value.location ||
      formData.parentId !== item.value.parentId ||
      formData.description !== item.value.description ||
      formData.latitude !== item.value.latitude ||
      formData.longitude !== item.value.longitude ||
      formData.locationTypeId !== item.value.locationTypeId ||
      formData.colorId !== item.value.colorId ||
      formData.ownerId !== item.value.ownerId ||
      formData.radius !== item.value.radius ||
      formData.customData !== item.value.customData ||
      formData.private !== item.value.private ||
      !areArraysEqual(formData.accessBusinessUnitIds, item.value.accessBusinessUnitIds)
    )
  }
})
const formTitle = computed(() => {
  if (isCreatingNewLocation.value) {
    return 'New location'
  } else {
    return `Edit ${item.value?.name || ''}`
  }
})

const isSaving = ref(false)
const isErrorSaving = computed(() => isUpdateError.value || isCreateError.value)
const errorMessage = computed(() => {
  if (isUpdateError.value && updateError.value) {
    return (updateError.value as { message: never }).message as string
  } else if (isCreateError.value && createError.value) {
    return (createError.value as { message: never }).message as string
  } else {
    return ''
  }
})
const getUserBySid = computed(() => store.getters['users/getBySid'])
const isHubUser = computed(() => store.getters['permissions/isHostOrganizationUser'])

const archivedChip = computed(() => (item.value && item.value?.isArchived ? 'Archived' : undefined))
const isParentLocation = computed(() => (item.value?.childCount ?? 0) > 0)
const title = computed(() =>
  formTitle.value === 'New item' ? 'New location' : formTitle.value || '',
)
const showParentLocationField = computed(
  () => parentFlag.value || (item.value && item.value.parentId !== null),
)
const rootSchema = computed<LocationTypeCustomFieldsSchema | undefined>(() => {
  return (
    locationType.value && (locationType.value.customFieldsSchema as LocationTypeCustomFieldsSchema)
  )
})
const schema = computed(() => rootSchema.value?.schema)
const uiSchema = computed(() => rootSchema.value?.uiSchema)
const contextualizedFormResolver = computed(() => {
  const currentUser = getUserBySid.value(auth.user.sub) || auth.user

  const context = new FormContext({
    hub: null,
    currentUser,
    additionalContext: '',
  })
  return new ContextualizedFormResolver(context)
})

function validate() {
  return validateCustomFields() && form.value?.isValid()
}

function validateCustomFields() {
  const resolvedSchema = contextualizedFormResolver.value.retrieveSchema(
    schema.value,
    schema.value,
    formData?.customData,
  )
  const resolvedData = resolveFormDataForValidation(formData?.customData)

  errorSchema.value = validateFormData(resolvedSchema, resolvedData)

  return isEmpty(errorSchema.value)
}

async function submit() {
  isSaving.value = true
  if (validate()) {
    await saveItem()
  }
  isSaving.value = false
}

function dismiss() {
  if (props.id === ID_NEW) {
    setSelectedLocation(null)
    // Editing an existing location clears the state in FieldViewReadonlyLocation
  }

  if (hasChanges.value) {
    const confirmation = window.confirm(
      'There are unsaved changes, are you sure you want to discard them?',
    )

    if (!confirmation) {
      return
    }
  }

  if (props.dismissTo) {
    router.push(props.dismissTo)
  } else {
    emit('dismiss')
  }
}

async function saveItem() {
  let locationId = props.id
  if (isCreatingNewLocation.value) {
    try {
      await createLocation({
        accessBusinessUnitIds: formData.accessBusinessUnitIds ?? [],
        colorId: formData.colorId ?? null,
        description: formData.description ?? '',
        latitude: formData.latitude ?? null,
        location: formData.location ?? null,
        locationTypeId: formData.locationTypeId ?? null,
        longitude: formData.longitude ?? null,
        ownerId: formData.ownerId ?? null,
        parentId: formData.parentId ?? null,
        radius: formData.radius ?? null,
        customData: formData.customData ?? null,
        inventory: formData.inventory
          ?.filter((item) => !!item.inventorySubTypeId && !!item.quantity)
          .map((item) => ({
            assetConditionId: item.inventorySubTypeId,
            quantity: item.quantity ? Number(item.quantity) : 0,
          })),
        private: formData.private ?? false,
      })
    } catch (e) {
      return
    }
    locationId = createResponse.value?.data
  } else {
    try {
      await updateLocation({
        id: Number(props.id),
        accessBusinessUnitIds: formData.accessBusinessUnitIds ?? null,
        colorId: formData.colorId ?? null,
        description: formData.description ?? '',
        locationTypeId: formData.locationTypeId ?? null,
        latitude: formData.latitude ?? null,
        location: formData.location ?? null,
        longitude: formData.longitude ?? null,
        ownerId: formData.ownerId ?? null,
        parentId: formData.parentId ?? null,
        radius: formData.radius ?? null,
        customData: formData.customData ?? null,
        isArchived: formData.isArchived ?? null,
        private: formData.private ?? false,
      })
    } catch (e) {
      return
    }
  }

  if (!isUpdateError.value && !isCreateError.value) {
    if (props.id !== ID_NEW) {
      await refetch()
      upsertModifiedLocation(item.value!)
    }

    if (props.redirectAfterSaveTo) {
      await router.push({
        ...props.redirectAfterSaveTo,
        params: {
          ...props.redirectAfterSaveTo.params,
          id: locationId.toString(),
        },
      })
    } else {
      emit('save')
    }
  }
}

async function onParentChange(parentId: number | number[] | undefined) {
  formData.parentId = (parentId ?? null) as number | null
  if (parentId === undefined || !isHubUser.value) {
    return
  }
  const parentLocation = (await getById(parentId as number)) as Location

  if (parentLocation && item.value) {
    formData.accessBusinessUnitIds = formData.accessBusinessUnitIds.filter((hubId: string) =>
      parentLocation.accessBusinessUnitIds.includes(hubId),
    )
  }
}

async function handleArchiveLocation() {
  const confirmation = window.confirm('Are you sure you want to archive this location?')
  if (confirmation && item.value) {
    try {
      if (formData.isArchived) {
        upsertModifiedLocation({
          ...item.value,
          isArchived: false,
        })
        await unarchiveLocation(parseInt(props.id.toString(), 10))
      } else {
        upsertModifiedLocation({ ...item.value, isArchived: true })
        await archiveLocation(parseInt(props.id.toString(), 10))
      }
      formData.isArchived = !formData.isArchived
      await refetch()
    } catch (e) {}

    if (item.value?.id) {
      await router.push(redirectAfterArchivalTo.value)
    }
  }
}

function handleUpdateLocationName(e: string) {
  formData.location = e
}

function handleUpdateLocationDescription(e: string) {
  formData.description = e
}

function handleLocationAdditionalInfoUpdate(e: { field: string; value: any }) {
  if (e.field === 'locationTypeId') {
    formData.locationTypeId = e.value
  } else if (e.field === 'colorId') {
    formData.colorId = e.value
  } else if (e.field === 'ownerId') {
    formData.ownerId = e.value
  } else if (e.field === 'private') {
    formData.private = e.value
  } else if (e.field === 'accessBusinessUnitIds') {
    formData.accessBusinessUnitIds = e.value
  }
}

function handleLocationLatLongUpdate(e: { field: string; value: never }) {
  if (e.field === 'latitude') {
    const numberValue = parseFloat(e.value)
    formData.latitude = isNaN(numberValue) ? undefined : numberValue
  } else if (e.field === 'longitude') {
    const numberValue = parseFloat(e.value)
    formData.longitude = isNaN(numberValue) ? undefined : numberValue
  } else if (e.field === 'radius') {
    const numberValue = parseInt(e.value, 10)
    formData.radius = isNaN(numberValue) ? undefined : numberValue
  }
}

function handleLocationSchemaFieldsUpdate(e: object) {
  formData.customData = e
}

function handleLocationHubAccessUpdate(e: { field: string; value: string[] }) {
  formData.accessBusinessUnitIds = e.value
}

function areArraysEqual(array1: string[], array2: string[]) {
  return array1.length === array2.length && array1.every((value, index) => value === array2[index])
}
</script>

<style scoped>
.focus-hubs-selector {
  border-style: solid;
  border-width: 2px;
  border-color: red;
  padding: 2px;
}
</style>
